import type { AutocaptureConfig, PostHog, PostHogConfig } from 'posthog-js';
import { env } from '@/env';
import { isBrowserLocalhost } from '@/lib/utils/runtime';

const AUTOCAPTURE_CONFIG: AutocaptureConfig = {
  dom_event_allowlist: ['click', 'submit', 'change'],
  element_allowlist: ['a', 'button', 'form', 'input', 'label', 'select', 'textarea'],
};

type PosthogInitConfig = Partial<PostHogConfig>;

type PosthogClient = PostHog & {
  __loaded?: boolean;
};

type PosthogModule = { default: PostHog };

let posthogModulePromise: Promise<PosthogModule> | null = null;

function loadPosthogModule(): Promise<PosthogModule> {
  if (!posthogModulePromise) {
    posthogModulePromise = import('posthog-js');
  }

  return posthogModulePromise;
}

let posthogClientPromise: Promise<PosthogClient> | null = null;

function loadPosthogClient(): Promise<PosthogClient> {
  if (!posthogClientPromise) {
    posthogClientPromise = loadPosthogModule().then(({ default: client }) => client as PosthogClient);
  }

  return posthogClientPromise;
}

const POSTHOG_API_HOST = env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com';
const POSTHOG_TOKEN = env.NEXT_PUBLIC_POSTHOG_KEY ?? null;

function getPosthogConfig(): PosthogInitConfig {
  const config: PosthogInitConfig = {
    api_host: POSTHOG_API_HOST,
    ui_host: null,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: AUTOCAPTURE_CONFIG,
    capture_performance: false,
  };

  return config;
}

export function shouldInitializePosthog(): boolean {
  if (globalThis.window === undefined) {
    return false;
  }

  if (!POSTHOG_TOKEN) {
    return false;
  }

  return !isBrowserLocalhost();
}

export async function ensurePosthogInitialized(): Promise<void> {
  if (!shouldInitializePosthog()) {
    return;
  }

  const client = await loadPosthogClient();

  if (client.__loaded) {
    return;
  }

  const token = POSTHOG_TOKEN;
  if (!token) {
    return;
  }

  client.init(token, getPosthogConfig());
}

export function getPosthogClient(): Promise<PosthogClient> {
  return loadPosthogClient();
}

export type { PosthogClient };
